'use client'

import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Snackbar } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'

import { HOLIDAYS_CONTACT } from '@/src/constants/holidays'
import { useHolidayContext } from '@/src/contexts'
import type { HolidayEnquirySource, HolidayPackage } from '@/src/types/holidays'
import type { CountryWithMCC } from '@/src/types/country'
import { useGetCountryDataQuery } from '@/src/api/rtkQuery/countryApi'
import { useSubmitHolidayEnquiryMutation } from '@/src/api/rtkQuery/holidays/holidayEnquiryApi'
import useClevertapForHolidays from '@/src/hooks/analytics/clevertap/holidays'
import useHolidaysGtmEvent from '@/src/hooks/analytics/gtm/holidays'
import useHolidayContacted from '@/src/hooks/holidays/useHolidayContacted'
import { HOLIDAY_ACTION, HOLIDAY_ACTION_TYPE } from '@/src/utils/analytics/holidays/holidayEvents'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { VARIANT } from '@/src/utils/buildUtils'
import { getPlatformName } from '@/src/utils/deviceType'
import { formatHolidayPrice } from '@/src/utils/holidays'

import { ENQUIRY_ANCHOR_ID } from '../mobileEnquiryBar'

import EnquiryCard from './enquiryCard'

/** Field ids for the page's one inline form; a second copy namespaces its own. Doubles as the
 *  `form_name` reported on the lead events, so the inline form and the sheet stay distinguishable. */
const FIELD_ID_PREFIX = 'holiday-enquiry'

/** `placement` on the contact event — the form, as opposed to the strip or the docked bar. */
const CONTACT_PLACEMENT = 'enquiry_card'

/** Why a field failed, reported alongside `field_name` on the lead-form-error event. */
const ERROR_REQUIRED = 'required'
const ERROR_INVALID = 'invalid'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
/** Long enough to be a real subscriber number, loose enough for every local format. */
const MOBILE_PATTERN = /^\d{6,15}$/

/** These packages sell out of the UAE, so the dial code starts there. */
const DEFAULT_MOBILE_COUNTRY_CODE = '971'

const BRAND_NAMES: Record<string, string> = { FLYIN: 'Flyin.com', CLEARTRIP: 'Cleartrip' }

type FieldKey = 'name' | 'email' | 'mobile' | 'promo'
type ConsentKey = 'deals' | 'contact' | 'terms'

interface EnquiryCardWrapperProps {
    enquiry?: HolidayPackage['enquiry']
    startingPrice?: HolidayPackage['summary']['startingPrice']
    /**
     * Set on a second copy of the form — the bottom sheet — so its fields and its anchor do not
     * collide with the inline one. Omitted, the form owns the page's `enquiry` anchor.
     */
    instanceId?: string
    isMobile: boolean
}

const EnquiryCardWrapper: React.FC<EnquiryCardWrapperProps> = ({
    enquiry,
    startingPrice,
    instanceId,
    isMobile
}) => {
    const t = useTranslations('holidaysPage')
    const commonT = useTranslations('common')
    const locale = useLocale()
    const { packageData, domainCountry } = useHolidayContext()
    const [submitEnquiry, { isLoading: isSubmitting }] = useSubmitHolidayEnquiryMutation()
    const { pushHolidayDetailsPageViewEvent, pushHolidayContactDetailsClickEvent } = useHolidaysGtmEvent()
    const { triggerHolidayUIActionEvent } = useClevertapForHolidays()
    const { isContacted, enquiryReference, markContacted } = useHolidayContacted()

    const formName = instanceId ?? FIELD_ID_PREFIX
    /** `holiday_lead_form_started` reports the first interaction only, once per mounted form. */
    const hasStartedRef = useRef(false)

    // The same country source the itinerary contact form uses, so dial codes, grouping and the
    // localised country names stay identical across the app.
    const { data: countryData, isLoading: countryLoading } = useGetCountryDataQuery()
    const countryOptions = useMemo(() => countryData ?? [], [countryData])

    const [values, setValues] = useState<Record<FieldKey, string>>({
        name: '',
        email: '',
        mobile: '',
        promo: ''
    })
    const [countryCode, setCountryCode] = useState(DEFAULT_MOBILE_COUNTRY_CODE)
    const [consents, setConsents] = useState<Record<ConsentKey, boolean>>({
        deals: true,
        contact: true,
        terms: false
    })
    const [errors, setErrors] = useState<Partial<Record<FieldKey | 'countryCode' | 'terms', string>>>({})
    const [failedOpen, setFailedOpen] = useState(false)

    const selectedCountry = useMemo(
        () => countryOptions.find(option => option.mobileCountryCode === countryCode) ?? null,
        [countryOptions, countryCode]
    )

    const markFormStarted = useCallback(
        (field: string) => {
            if (hasStartedRef.current) return
            hasStartedRef.current = true

            triggerHolidayUIActionEvent(HOLIDAY_ACTION.LEAD_FORM_STARTED, {
                action_type: HOLIDAY_ACTION_TYPE.FORM,
                form_name: formName,
                first_field: field
            })
        },
        [formName, triggerHolidayUIActionEvent]
    )

    const handleFieldChange = useCallback(
        (field: FieldKey, value: string) => {
            markFormStarted(field)
            setValues(current => ({ ...current, [field]: value }))
            setErrors(current => ({ ...current, [field]: undefined }))
        },
        [markFormStarted]
    )

    const handleCountryChange = useCallback(
        (country: CountryWithMCC | null) => {
            markFormStarted('countryCode')
            setCountryCode(country?.mobileCountryCode ?? '')
            setErrors(current => ({ ...current, countryCode: undefined }))
        },
        [markFormStarted]
    )

    const handleConsentChange = useCallback(
        (key: ConsentKey, checked: boolean) => {
            markFormStarted(key)
            setConsents(current => ({ ...current, [key]: checked }))
            if (key === 'terms') setErrors(current => ({ ...current, terms: undefined }))
        },
        [markFormStarted]
    )

    const handleContactClick = useCallback(() => {
        triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTACT_CLICKED, {
            action_type: HOLIDAY_ACTION_TYPE.CONTACT,
            channel: 'email',
            placement: CONTACT_PLACEMENT
        })
    }, [triggerHolidayUIActionEvent])

    const handleSubmit = useCallback(async () => {
        type ErrorKey = FieldKey | 'countryCode' | 'terms'
        const next: Partial<Record<ErrorKey, string>> = {}
        /** Why each failing field failed, so the error event says more than which field it was. */
        const reasons: Partial<Record<ErrorKey, string>> = {}

        if (!values.name.trim()) {
            next.name = t('errorNameRequired')
            reasons.name = ERROR_REQUIRED
        }
        if (!EMAIL_PATTERN.test(values.email.trim())) {
            next.email = t('errorEmailInvalid')
            reasons.email = values.email.trim() ? ERROR_INVALID : ERROR_REQUIRED
        }

        const mobile = values.mobile.replace(/\D/g, '')
        if (!mobile) {
            next.mobile = t('errorMobileRequired')
            reasons.mobile = ERROR_REQUIRED
        } else if (!MOBILE_PATTERN.test(mobile)) {
            next.mobile = t('errorMobileInvalid')
            reasons.mobile = ERROR_INVALID
        }

        if (!countryCode) {
            next.countryCode = t('errorCountryCodeRequired')
            reasons.countryCode = ERROR_REQUIRED
        }
        if (!consents.terms) {
            next.terms = t('errorTermsRequired')
            reasons.terms = ERROR_REQUIRED
        }

        setErrors(next)

        const invalidFields = Object.keys(next) as ErrorKey[]
        if (invalidFields.length > 0) {
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.LEAD_FORM_ERROR, {
                action_type: HOLIDAY_ACTION_TYPE.FORM,
                form_name: formName,
                field_name: invalidFields.join(','),
                error_type: invalidFields.map(field => reasons[field] ?? ERROR_INVALID).join(','),
                error_count: invalidFields.length
            })
            return
        }

        if (!packageData) return

        let reference: string
        try {
            const response = await submitEnquiry({
                // DESKTOP, PWA, or IOS / ANDROID inside the apps' WebView.
                source: getPlatformName(getDeviceType()) as HolidayEnquirySource,
                domain: domainCountry,
                lang: locale,
                collectionSlug: packageData.collectionSlug,
                packageSlug: packageData.slug,
                name: values.name.trim(),
                email: values.email.trim(),
                mobileCountryCode: countryCode,
                mobileNumber: mobile,
                promoCode: values.promo.trim(),
                pageUrl: window.location.href,
                consents
            }).unwrap()
            reference = response.tripId
        } catch {
            setFailedOpen(true)
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.LEAD_FAILED, {
                action_type: HOLIDAY_ACTION_TYPE.FORM,
                form_name: formName
            })
            return
        }

        const contactFields = {
            formName,
            contactName: values.name.trim(),
            contactEmail: values.email.trim(),
            mobileCountryCode: countryCode,
            mobileNumber: mobile,
            promoCodePresent: Boolean(values.promo.trim()),
            emailOptIn: consents.deals,
            contactAuthorisation: consents.contact
        }

        pushHolidayDetailsPageViewEvent({
            packageSlug: packageData.slug,
            collectionSlug: packageData.collectionSlug,
            contacted: true
        })
        pushHolidayContactDetailsClickEvent({
            packageSlug: packageData.slug,
            collectionSlug: packageData.collectionSlug,
            ...contactFields
        })
        // The package itself — name, destination, dates, price — rides on the page context.
        triggerHolidayUIActionEvent(HOLIDAY_ACTION.LEAD_SUBMITTED, {
            action_type: HOLIDAY_ACTION_TYPE.FORM,
            form_name: formName,
            mobile_country_code: countryCode,
            promo_code_present: Boolean(values.promo.trim()),
            email_opt_in: consents.deals,
            contact_authorisation: consents.contact
        })
        markContacted(reference)
    }, [
        values,
        countryCode,
        consents,
        packageData,
        domainCountry,
        locale,
        submitEnquiry,
        pushHolidayDetailsPageViewEvent,
        pushHolidayContactDetailsClickEvent,
        triggerHolidayUIActionEvent,
        formName,
        markContacted,
        t
    ])

    const brand = BRAND_NAMES[VARIANT] ?? BRAND_NAMES.FLYIN

    let sentMessage: string | undefined
    if (enquiryReference) sentMessage = t('enquirySentWithReference', { reference: enquiryReference })
    else if (isContacted) sentMessage = t('enquirySent')

    return (
        <>
            <EnquiryCard
                anchorId={instanceId ?? ENQUIRY_ANCHOR_ID}
                idPrefix={instanceId ?? FIELD_ID_PREFIX}
                title={enquiry?.title ?? t('bookToday')}
                subtitle={enquiry?.subtitle}
                priceLabel={enquiry?.priceLabel ?? t('adultFrom')}
                price={startingPrice ? formatHolidayPrice(startingPrice, locale) : undefined}
                labels={{
                    fullName: t('fullNameLabel'),
                    namePlaceholder: t('namePlaceholder'),
                    email: t('emailLabel'),
                    emailPlaceholder: t('emailPlaceholder'),
                    mobile: t('mobileLabel'),
                    mobilePlaceholder: t('mobilePlaceholder'),
                    promo: t('promoLabel'),
                    promoPlaceholder: t('promoPlaceholder'),
                    select: t('countryCodeLabel')
                }}
                values={values}
                errors={errors}
                onFieldChange={handleFieldChange}
                countryOptions={countryOptions}
                countryLoading={countryLoading}
                selectedCountry={selectedCountry}
                onCountryChange={handleCountryChange}
                countryOptionLabel={option =>
                    `+ ${option.mobileCountryCode} (${option[locale as keyof CountryWithMCC]})`
                }
                countryValueLabel={option => `+ ${option.mobileCountryCode}`}
                countryGroupBy={option => commonT(option.category)}
                consents={consents}
                consentLabels={{
                    deals: t('consentDeals'),
                    contact: t('consentContact', { brand }),
                    terms: t('consentTerms', { brand })
                }}
                onConsentChange={handleConsentChange}
                submitLabel={t('contactMe')}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                sentMessage={sentMessage}
                privacyNote={t('privacyNote')}
                expertLabel={t('talkToExpert')}
                phoneLabel={HOLIDAYS_CONTACT.phoneLabel}
                emailAddress={HOLIDAYS_CONTACT.email}
                emailHref={`mailto:${HOLIDAYS_CONTACT.email}`}
                onContactClick={handleContactClick}
                isMobile={isMobile}
            />

            <Snackbar
                open={failedOpen}
                onClose={() => setFailedOpen(false)}
                autoHideDuration={4000}
                message={t('errorEnquiryFailed')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ bottom: { xs: 96 } }}
            />
        </>
    )
}

export { EnquiryCardWrapper }
export default EnquiryCardWrapper
